<template>
	<div data-component="study-deal-item">
		<div data-element="base-row">
			<p data-element="name">
				{{ getDealName }}
			</p>
			<div data-element="actions">
				<V2Button
					v-if="getIsShowAdditionalActions && getDealRecording"
					size="xs"
					fit
					@click="handleShowDealRecording"
					variant="red"
					icon
				>
					<icon class="play-icon" icon="v2-deal-play" colour="transparent" />
				</V2Button>
			</div>
			<icon
				:icon="getStatusIcon"
				colour="transparent"
				data-element="deal-icon"
			/>
			<span v-if="getBonusTricks" data-element="bonus-tricks-icon">{{ getBonusTricks }}</span>
		</div>
		<div
			data-element="contract-and-declarer"
			v-if="getShowContractAndDeclarer"
		>
			<div data-element="label">
				Contract:
			</div>
			<contract-and-declarer
				:contract="getContract"
				:declarer="getDeclarer"
				:isDoubled="isDoubled"
				:isReDoubled="isReDoubled"
			/>
		</div>
		<div
			v-if="getIsShowAdditionalActions"
			data-element="completed-row"
		>
			<V2Button
				v-if="getDealAnalysis"
				size="xs"
				:url="getDealAnalysis.url"
			>
				Show deal analysis
			</V2Button>
		</div>
		<div class="play-controls">
			<V2Button
				v-if="getReplayIsVisible"
				variant="outline"
				:btnId="deal.id"
				@click="handlePlayDeal"
			>
				Replay
			</V2Button>
			<V2Button
				v-else
				:disabled="!getPlayIsVisible"
				:btnId="deal.id"
				@click="handlePlayDeal"
			>
				{{ getPlayIsVisible ? 'Play now' : 'Locked' }}
			</V2Button>
		</div>
	</div>
</template>

<script>

	import Icon from '@/components/ui/Icon';
	import V2Button from '@/components/v2/ui/V2Button';
	import ContractAndDeclarer from '@/components/diagrams/ContractAndDeclarer';
	import routeParams from '@/mixins/routeParams';
	import { dealAnalysisType, dealRecordingType } from '@/consts';

	export default {
		components: {
			Icon,
			V2Button,
			ContractAndDeclarer
		},
		mixins: [routeParams],
		props: {
			deal: {
				type: Object,
				default: undefined
			},
			index: {
				type: Number,
				required: true
			},
			isDisabled: {
				type: Boolean,
				default: false
			}
		},
		data: () => ({}),
		computed: {
			getIsStoreView () {
				return this.getContext === 'dealStore';
			},
			getIsTeacherView () {
				return this.getContext === 'teaching';
			},
			getIsAdminView () {
				return this.getContext === 'admin';
			},
			getIsGrantedAccessView () {
				return this.getIsTeacherView || this.getIsAdminView;
			},
			getIsStudentView () {
				return this.getContext === 'myDeals';
			},
			getDealName () {
				return this.deal?.name || `Deal ${this.index + 1}`;
			},
			getStatus () {
				return this.deal?.status;
			},
			getIsCompleted () {
				return this.deal?.isComplete;
			},
			getBonusTricks () {
				if (this.getIsGrantedAccessView) {
					return false;
				}
				return this.deal?.bonusTricks || 0;
			},
			getMadeMaximumTricks () {
				return this.deal?.madeMaximumTricks || false;
			},
			getStatusIcon () {
				if (this.getIsStoreView) {
					return 'upcoming';
				}
				if (this.getIsGrantedAccessView) {
					return 'ongoing';
				}
				if (this.getIsCompleted) {
					return this.getMadeMaximumTricks ? 'max-tricks' : this.getStatus;
				}

				return this.isDisabled ? 'upcoming' : 'ongoing';
			},
			getPlayIsVisible () {
				if (this.getIsStoreView) {
					return false;
				}
				if (this.getIsGrantedAccessView) {
					return true;
				}
				return !this.isDisabled;
			},
			getReplayIsVisible () {
				if (this.getIsGrantedAccessView) {
					return false;
				}
				return this.getIsCompleted;
			},
			getDealMedia () {
				return this.deal?.media || [];
			},
			getDealAnalysis () {
				return this.getDealMedia.find(item => item.config?.type === dealAnalysisType);
			},
			getDealRecording () {
				return this.getDealMedia.find(item => item.config?.type === dealRecordingType);
			},
			getIsShowAdditionalActions () {
				if (this.getIsStoreView) {
					return false;
				}
				const rules = [!!(this.getDealAnalysis || this.getDealRecording)];
				if (this.getIsStudentView) {
					rules.push(this.getIsCompleted);
				}

				return rules.every(r => r);
			},
			getContract () {
				return this.deal?.contract;
			},
			getDeclarer () {
				return this.deal?.declarer;
			},
			isDoubled () {
				if (this.deal?.deal) {
					const filteredBids = this.deal.deal.auction.bids.filter((item) => item !== 'P');
					return filteredBids[filteredBids.length - 1] === 'X';
				}
				return false;
			},

			isReDoubled () {
				if (this.deal?.deal) {
					const filteredBids = this.deal.deal.auction.bids.filter((item) => item !== 'P');
					return filteredBids[filteredBids.length - 1] === 'XX';
				}
				return false;
			},
			getShowContractAndDeclarer () {
				if (this.getIsGrantedAccessView) {
					return true;
				}
				if (this.getIsCompleted) {
					return this.getContract && this.getDeclarer;
				}
				return false;
			}
		},
		methods: {
			handlePlayDeal () {
				this.$emit('onPlayDeal');
			},
			handleShowDealRecording () {
				if (this.getDealRecording) {
					this.$emit('onShowDealRecording', this.getDealRecording.url);
				}
			}
		}
	};

</script>

<style lang="scss" scoped>

  .play-icon {
    width: 16px;
    height: 16px;
  }

	[data-component='study-deal-item'] {
		display: flex;
		flex-direction: column;
		padding: 20px;
		border: 1px solid $c-gray-d2;
		border-radius: 4px;
		justify-content: space-between;
		min-height: 158px;

		[data-element='base-row'] {
			display: flex;
			align-items: center;

			[data-element='bonus-tricks-icon'] {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				background-color: #32A03D;
				border-radius: 50%;
				width: rem(18);
				height: rem(18);
				margin-left: rem(-4);
				border: 1px solid $c-white-l;

				@include font(12px, $c-white-l, 500, 16px);
			}

			[data-element='deal-icon'] {
				flex-shrink: 0;
				width: rem(28);
				height: auto;
				margin-left: rem(8);
			}

			[data-element='name'] {
				overflow: hidden;
				flex-shrink: 1;

        @include font;

				margin-right: rem(24);
				white-space: nowrap;
				text-overflow: ellipsis;
			}

			[data-element='actions'] {
				margin-left: auto;
        display: flex;
        gap: 8px;

				.btn {
					border-radius: 50%;
				}
			}
		}

		[data-element='contract-and-declarer'] {
			display: flex;
			align-items: center;

			[data-element='label'] {
				margin-right:0.5em;
			}
		}

		[data-element='completed-row'] {
			display: flex;
      flex-direction: column;
			align-items: center;
			margin-top: rem(8);
      gap: 8px;

			[data-component='btn'] {
        width: 100%;
				&:not(:last-child) {
					margin-right: rem(8);
				}
			}
		}

		.play-controls {
			margin-top: auto;
		}

	}

</style>
